import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { useToast } from './ui/use-toast';
import { updateSchedule, getScheduleById, RaidSchedule } from '@/api/scheduleApi';
import { ko } from 'date-fns/locale';

interface ScheduleEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduleId: number | null;
  onSuccess: () => void;
}

export default function ScheduleEditModal({ open, onOpenChange, scheduleId, onSuccess }: ScheduleEditModalProps) {
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  // 폼 데이터
  const [schedule, setSchedule] = useState<RaidSchedule | null>(null);
  const [content, setContent] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState('');

  /**
   * 스케줄 정보 로드
   */
  useEffect(() => {
    if (open && scheduleId) {
      loadSchedule(scheduleId);
    }
  }, [open, scheduleId]);

  const loadSchedule = async (id: number) => {
    try {
      setLoadingSchedule(true);
      const data = await getScheduleById(id);
      setSchedule(data);

      // 시작 시간 파싱 (ISO format: "2025-12-24T21:00:00")
      const [dateStr, timeStr] = data.startTime.split('T');

      // Date 객체로 변환
      const [year, month, day] = dateStr.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day);
      setSelectedDate(dateObj);

      setStartTime(timeStr.substring(0, 5)); // "21:00"
      setContent(data.content || '');
    } catch (error) {
      console.error('스케줄 로드 실패:', error);
      toast({
        title: '오류',
        description: '스케줄 정보를 불러오는데 실패했습니다.',
        variant: 'destructive',
      });
    } finally {
      setLoadingSchedule(false);
    }
  };

  /**
   * 폼 제출
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!scheduleId) return;

    // 유효성 검사
    if (!selectedDate) {
      toast({
        title: '오류',
        description: '날짜를 선택해주세요.',
        variant: 'destructive',
      });
      return;
    }

    if (!startTime) {
      toast({
        title: '오류',
        description: '시간을 입력해주세요.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      // 날짜 포맷팅
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const startTimeStr = `${year}-${month}-${day} ${startTime}`;

      await updateSchedule(scheduleId, {
        content,
        startTime: startTimeStr,
      });

      toast({
        title: '성공',
        description: '스케줄이 수정되었습니다.',
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('스케줄 수정 실패:', error);
      toast({
        title: '오류',
        description: '스케줄 수정에 실패했습니다.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-4 pt-4 pb-3">
          <DialogTitle className="text-lg">레이드 스케줄 수정</DialogTitle>
          <DialogDescription className="text-sm">
            {schedule && `${schedule.raidTitle} 일정을 수정합니다`}
          </DialogDescription>
        </DialogHeader>

        {loadingSchedule ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">로딩 중...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="space-y-4 px-4 pb-4">
            {/* 레이드 정보 (읽기 전용) */}
            {schedule && (
              <div className="bg-accent/20 border border-accent rounded-lg p-3 space-y-1">
                <p className="text-sm font-semibold">{schedule.raidTitle}</p>
                <p className="text-xs text-muted-foreground">
                  레이드 종류는 수정할 수 없습니다
                </p>
              </div>
            )}

            {/* 날짜 선택 */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">날짜 선택 *</Label>
              <div className="border rounded-md p-3 flex justify-center bg-muted/30">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={ko}
                  className="rounded-md w-full text-sm"
                />
              </div>
            </div>

            {/* 시간 선택 */}
            <div className="space-y-2">
              <Label htmlFor="startTime" className="text-sm font-semibold">시간 선택 *</Label>
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
                className="text-sm h-9"
              />
              <p className="text-[11px] text-muted-foreground/80">
                예: 21:00, 14:30 등
              </p>
            </div>

            {/* 메모 */}
            <div className="space-y-2">
              <Label htmlFor="content" className="text-sm font-semibold">메모 (선택, 최대 40자)</Label>
              <Textarea
                id="content"
                placeholder="ex) 숙제팟"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={40}
                rows={3}
                className="resize-none text-sm"
              />
              <p className="text-[11px] text-muted-foreground/80 text-right">{content.length}/40</p>
            </div>

            </div>

            {/* 하단 버튼 */}
            <div className="flex gap-2 px-4 py-3 border-t bg-muted/30">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
                className="flex-1 h-9 text-sm"
              >
                취소
              </Button>
              <Button type="submit" disabled={loading} className="flex-1 h-9 text-sm">
                {loading ? '수정 중...' : '수정'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
